import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { isPixelConfigured } from "@/utils/metaPixel";
import { Instagram, Radar, ShieldCheck, Trash2, Plus, Play } from "lucide-react";

const inputCls = "bg-black/30 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-[color:var(--hs-focus)]";

export const SettingsTab = () => {
  const [posts, setPosts] = useState([]);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/instagram/posts");
      setPosts(data.items || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addPost = async () => {
    if (!url.trim()) {
      toast.error("Paste an Instagram post or reel link first");
      return;
    }
    setBusy(true);
    try {
      await api.post("/instagram/posts", { url: url.trim(), label: label.trim() });
      toast.success("Added to the showcase");
      setUrl("");
      setLabel("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that link");
    } finally {
      setBusy(false);
    }
  };

  const removePost = async (id) => {
    try {
      await api.delete(`/instagram/posts/${id}`);
      toast.success("Removed from showcase");
      load();
    } catch (e) {
      toast.error("Could not remove post");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start" data-testid="crm-settings-tab">
      {/* Instagram Showcase manager */}
      <Card className="bg-[color:var(--hs-bg-1)] border-white/10 rounded-2xl lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2.5 text-white text-base">
            <Instagram className="h-5 w-5 text-[color:var(--hs-lime)]" /> Instagram Showcase
            <Badge variant="outline" className="bg-[rgba(202,255,26,0.12)] text-[#caff1a] border-[rgba(202,255,26,0.25)] text-[10px] uppercase">
              {posts.length} curated
            </Badge>
          </CardTitle>
          <CardDescription className="text-white/50">
            Paste links to your Instagram posts/reels — they appear on the landing page in a side-scrollable 4:5
            carousel, in the order added. No Instagram login or token needed. When empty, branded showreel stills are
            shown instead.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="settings-ig-url-input"
              placeholder="https://www.instagram.com/reel/XXXXXXXX/ or /p/XXXXXXXX/"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={`${inputCls} h-11 flex-1`}
            />
            <Input
              data-testid="settings-ig-label-input"
              placeholder="Label (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className={`${inputCls} h-11 sm:w-[200px]`}
            />
            <Button
              onClick={addPost}
              disabled={busy}
              data-testid="settings-ig-add-button"
              className="h-11 bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl btn-lift shrink-0"
            >
              <Plus className="h-4 w-4 mr-2" /> Add
            </Button>
          </div>

          <div className="space-y-2" data-testid="settings-ig-posts-list">
            {posts.length === 0 && (
              <p className="text-sm text-white/40 py-4 text-center">
                No posts curated yet — the landing page is showing branded showreel stills.
              </p>
            )}
            {posts.map((post) => (
              <div key={post.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/25 px-4 py-3">
                <span className="h-8 w-8 shrink-0 rounded-lg bg-[rgba(202,255,26,0.1)] border border-[rgba(202,255,26,0.25)] flex items-center justify-center">
                  {post.kind === "reel" ? <Play className="h-3.5 w-3.5 text-[color:var(--hs-lime)]" /> : <Instagram className="h-3.5 w-3.5 text-[color:var(--hs-lime)]" />}
                </span>
                <div className="min-w-0 flex-1">
                  <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-sm text-white/85 hover:text-[color:var(--hs-lime)] truncate block">
                    {post.url}
                  </a>
                  <p className="font-mono-ui text-[10px] uppercase tracking-wider text-white/35">
                    {post.kind}{post.label ? ` · ${post.label}` : ""} · added {post.created_at?.slice(0, 10)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removePost(post.id)}
                  data-testid={`settings-ig-delete-${post.id}`}
                  className="text-white/40 hover:text-[#ff7878] hover:bg-white/5 shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Meta Pixel */}
      <Card className="bg-[color:var(--hs-bg-1)] border-white/10 rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2.5 text-white text-base">
            <Radar className="h-5 w-5 text-[color:var(--hs-lime)]" /> Meta Ads Pixel
            {isPixelConfigured() ? (
              <Badge variant="outline" className="bg-[rgba(120,255,180,0.12)] text-[#78ffb4] border-[rgba(120,255,180,0.25)] text-[10px] uppercase">Active</Badge>
            ) : (
              <Badge variant="outline" className="bg-[rgba(255,204,102,0.12)] text-[#ffcc66] border-[rgba(255,204,102,0.25)] text-[10px] uppercase">Pixel-ready</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-white/50">
            PageView fires on load and Lead fires on every successful consultation — once your Pixel ID is configured.
            Per DPDP compliance, the Pixel only runs for visitors who accept the consent banner.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border border-white/10 bg-black/25 p-3.5 text-xs text-white/45 leading-relaxed space-y-2">
            <p className="text-white/70 font-medium">To activate:</p>
            <p>1. Grab your Pixel ID from Meta Events Manager (business.facebook.com → Events Manager).</p>
            <p>2. Share it with the Emergent agent (or set <code className="text-[color:var(--hs-lime)]">REACT_APP_META_PIXEL_ID</code> in the frontend environment).</p>
            <p>3. Redeploy — Lead events start flowing into your ad account.</p>
          </div>
        </CardContent>
      </Card>

      {/* DPDP Compliance */}
      <Card className="bg-[color:var(--hs-bg-1)] border-white/10 rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2.5 text-white text-base">
            <ShieldCheck className="h-5 w-5 text-[color:var(--hs-lime)]" /> Data Protection (DPDP Act 2023)
            <Badge variant="outline" className="bg-[rgba(120,255,180,0.12)] text-[#78ffb4] border-[rgba(120,255,180,0.25)] text-[10px] uppercase">Compliant</Badge>
          </CardTitle>
          <CardDescription className="text-white/50">
            How this platform stays compliant with India's Digital Personal Data Protection Act, 2023.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border border-white/10 bg-black/25 p-3.5 text-xs text-white/45 leading-relaxed space-y-2">
            <p>• Explicit consent checkbox on the intake form — every website lead stores consent timestamp + policy version.</p>
            <p>• Analytics &amp; Meta Pixel are OFF until the visitor accepts the consent banner. No IP addresses stored.</p>
            <p>• Public <a href="/privacy" className="text-[color:var(--hs-lime)] underline">Privacy Policy</a> covers notice, purpose, rights, grievance officer (info@holdstill.in).</p>
            <p>• Erasure requests: delete the lead from the Leads tab — interactions &amp; follow-ups are removed with it.</p>
            <p>• CRM access restricted to Google-verified team sign-ins; data encrypted in transit (HTTPS).</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
